import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowSmDownIcon, ArrowSmUpIcon } from "@heroicons/react/outline";
import { classNames } from "../../Utils";
import { Button } from "../../Components/Button";
import { Pagination } from "../../Components/Pagination";
import Badge from "../../Components/Badge";
import ErrorStateTable from "../../Components/ErrorStateTable";
import {
  requestConsultantTransactionList,
  requestGetConsultant,
  useAuthState,
  useConsultantDispatch,
  useConsultantState,
} from "../../Context";
import TableSkeleton from "../../Components/TableSkeleton";
import { NumericFormat } from "react-number-format";
import { Alert } from "../../Components/Alert";
import { useNavigate } from "react-router-dom";

const consultanTransactionStatuses = [
  { value: "PENDING", label: "Pending" },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILED", label: "Failed" },
];

export const ConsultanTransactionList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useConsultantDispatch();
  const { user } = useAuthState();
  const {
    consultant,
    consultantTransactions,
    transactionsLoading,
    transactionsError,
    nextTokens,
    loading: consultantLoading,
    error: consultantError,
  } = useConsultantState();

  const [page, setpage] = useState(1);
  const [ratingDifference, setRatingDifference] = useState("");
  const [averageResponseDifference, setAverageResponseDifference] =
    useState("");

  const statusBadgeMap = {
    PENDING: { type: "alert" },
    SUCCESS: { type: "success" },
    FAILED: { type: "danger" },
  };

  useEffect(() => {
    if (user.id) {
      requestGetConsultant(dispatch, { userID: user.id });
    }
  }, [dispatch, user.id]);

  useEffect(() => {
    if (consultant.id) {
      requestConsultantTransactionList(dispatch, {
        page: 1,
        consultantID: consultant.id,
        limit: 10,
      });
    }
  }, [dispatch, consultant.id]);

  useEffect(() => {
    const ratingDifferenceValue = calculateAndFormatDifferenceRating(
      consultant.rating,
      consultant.lastRating
    );

    const averageResponseDifferenceValue = calculateAndFormatDifferenceResponse(
      consultant.averageResponse,
      consultant.lastAverageResponse
    );

    setRatingDifference(ratingDifferenceValue);
    setAverageResponseDifference(averageResponseDifferenceValue);
  }, [consultant]);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestConsultantTransactionList(dispatch, {
        page: nextPage,
        limit: 10,
        consultantID: consultant.id,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestConsultantTransactionList(dispatch, {
        page: prevPage,
        consultantID: consultant.id,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestConsultantTransactionList(dispatch, {
        page: selectedPage,
        consultantID: consultant.id,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  const formatAverageResponseTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const hoursText = hours > 0 ? `${hours} ${t("hours")}` : "";
    const minutesText = minutes > 0 ? `${minutes} ${t("minutes")}` : "";

    return `${hoursText} ${minutesText}`.trim();
  };

  const calculateAndFormatDifferenceResponse = (currentValue, lastValue) => {
    const difference = ((lastValue - currentValue) / lastValue) * 100;
    return isFinite(difference) ? difference.toFixed(2) : 0;
  };

  const calculateAndFormatDifferenceRating = (currentValue, lastValue) => {
    const difference = ((currentValue - lastValue) / lastValue) * 100;
    return isFinite(difference) ? difference.toFixed(2) : 0;
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Button
          className="w-full md:w-auto"
          type="button"
          title={t("Withdraw Balance")}
          onClick={() => navigate(`/consultant-withdraw`)}
        />
      </div>
      {consultantError ? (
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert
            type="error"
            title="Error"
            errors={consultantError?.errors}
            message={consultantError?.message || "Consultant not found."}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 rounded-lg bg-white overflow-hidden shadow divide-y divide-gray-200 md:grid-cols-4 md:divide-y-0 md:divide-x">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-base font-normal text-gray-500">
              {t("Main Balance")}
            </div>
            {consultantLoading ? (
              <div className="mt-1 animate-pulse w-32 h-6 bg-gray-300 rounded-md"></div>
            ) : (
              <div className="mt-1 text-xl font-semibold text-blue-600">
                <NumericFormat
                  displayType={"text"}
                  thousandSeparator={"."}
                  value={consultant.balance || 0}
                  decimalSeparator={","}
                  prefix={"Rp"}
                />
              </div>
            )}
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="text-base font-normal text-gray-500">
              {t("Pending Balance")}
            </div>
            {consultantLoading ? (
              <div className="mt-1 animate-pulse w-32 h-6 bg-gray-300 rounded-md"></div>
            ) : (
              <div className="mt-1 items-baseline text-xl font-semibold text-blue-600">
                <NumericFormat
                  displayType={"text"}
                  thousandSeparator={"."}
                  value={consultant.pendingBalance || 0}
                  decimalSeparator={","}
                  prefix={"Rp"}
                />
              </div>
            )}
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="text-base font-normal text-gray-500">
              {t("Rating")}
            </div>
            <div className="mt-1 flex justify-between items-baseline md:block lg:flex whitespace-nowrap gap-2">
              <div className="flex items-baseline text-xl font-semibold text-blue-600">
                {consultant.rating || 0}
                <span className="ml-2 text-sm font-medium text-gray-500">
                  {t("from")} {consultant.lastRating || 0}
                </span>
              </div>
              {ratingDifference > 0 && (
                <div
                  className={classNames(
                    consultant.rating > consultant.lastRating ||
                      consultant.rating === consultant.lastRating
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800",
                    "inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0"
                  )}
                >
                  {consultant.rating > consultant.lastRating ||
                  consultant.rating === consultant.lastRating ? (
                    <ArrowSmUpIcon
                      className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                  ) : (
                    <ArrowSmDownIcon
                      className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  )}
                  {ratingDifference}%
                </div>
              )}
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="text-base font-normal text-gray-500">
              {t("Response Time")}
            </div>
            <div className="mt-1 flex flex-wrap justify-between items-baseline md:block lg:flex gap-2">
              {consultant.averageResponse > 0 && (
                <div className="flex items-baseline text-xl font-semibold text-blue-600 whitespace-nowrap">
                  {formatAverageResponseTime(consultant.averageResponse)}
                  {consultant.lastAverageResponse > 0 ? (
                    <span className="ml-2 text-sm font-medium text-gray-500">
                      {t("from")}{" "}
                      {formatAverageResponseTime(
                        consultant.lastAverageResponse
                      )}
                    </span>
                  ) : null}
                </div>
              )}
              {averageResponseDifference > 0 && (
                <div
                  className={classNames(
                    consultant.averageResponse <
                      consultant.lastAverageResponse ||
                      consultant.averageResponse ===
                        consultant.lastAverageResponse
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800",
                    "inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0"
                  )}
                >
                  {consultant.averageResponse <
                    consultant.lastAverageResponse ||
                  consultant.averageResponse ===
                    consultant.lastAverageResponse ? (
                    <ArrowSmUpIcon
                      className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                  ) : (
                    <ArrowSmDownIcon
                      className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  )}
                  {averageResponseDifference} %
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Category")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Status")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Voucher")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Fee")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Income")}
                    </th>
                  </tr>
                </thead>
                {transactionsLoading ? (
                  <TableSkeleton rows={3} columns={5} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {consultantTransactions.length > 0 ? (
                      consultantTransactions.map((consultantTransaction) => {
                        const statusLabel =
                          consultanTransactionStatuses.find(
                            (status) =>
                              status.value === consultantTransaction.status
                          )?.label || "";
                        return (
                          <tr key={consultantTransaction.id}>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {consultantTransaction?.consultation?.category
                                ?.name || t("Withdrawal Balance")}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              <Badge
                                title={t(statusLabel)}
                                type={
                                  statusBadgeMap[consultantTransaction.status]
                                    ?.type
                                }
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {consultantTransaction?.voucher || t("-")}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                thousandSeparator={"."}
                                value={consultantTransaction.fee || 0}
                                decimalSeparator={","}
                                prefix={"Rp"}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                thousandSeparator={"."}
                                value={consultantTransaction.total || 0}
                                decimalSeparator={","}
                                prefix={"Rp"}
                              />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <ErrorStateTable
                        colSpan={12}
                        error={transactionsError}
                        emptyTitle={"No Consultant Transactions"}
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
